import axios from 'axios';
import Auth from '../../auth/auth';

export const myDelegationsActions = {
  LOAD_REQUEST: 'MY_DELEGATIONS_LOAD_REQUEST',
  LOAD_SUCCESS: 'MY_DELEGATIONS_LOAD_SUCCESS',
  LOAD_FAILURE: 'MY_DELEGATIONS_LOAD_FAILURE',
  LOAD_USERS_SUCCESS: 'MY_DELEGATIONS_LOAD_USERS_SUCCESS',
  MUTATION_SUCCESS: 'MY_DELEGATIONS_MUTATION_SUCCESS',
  MUTATION_FAILURE: 'MY_DELEGATIONS_MUTATION_FAILURE',
  CLEAR_MESSAGE: 'MY_DELEGATIONS_CLEAR_MESSAGE',

  // Slice 4c — mockup screens S6 and S7. A SEPARATE set of actions and a separate slice of state
  // from the five above, which drive the INCUMBENT ownership delegations (`ownership_delegations`,
  // routes/userProfile.js). §1.2: they are different objects — one lends ownership of a resource,
  // the other lends (permission, scope) pairs under R3/R4/R5 — and merging the two lists would be
  // the false equivalence the audit panel's subject-id column already cost this programme once.
  ACCESS_LOAD_SUCCESS: 'MY_DELEGATIONS_ACCESS_LOAD_SUCCESS',
  ACCESS_DELEGABLE_SUCCESS: 'MY_DELEGATIONS_ACCESS_DELEGABLE_SUCCESS',
  ACCESS_DELEGATES_SUCCESS: 'MY_DELEGATIONS_ACCESS_DELEGATES_SUCCESS',
  // The picker is gated on `access.delegation:create`, which most users do not hold. A 403 here is
  // not an error to show — it is the normal state of an un-provisioned user, and §3.6 requires the
  // drawer to say so rather than fail.
  ACCESS_NOT_PROVISIONED: 'MY_DELEGATIONS_ACCESS_NOT_PROVISIONED',
  ACCESS_DRAWER_OPEN: 'MY_DELEGATIONS_ACCESS_DRAWER_OPEN',
  ACCESS_DRAWER_CLOSE: 'MY_DELEGATIONS_ACCESS_DRAWER_CLOSE',
  ACCESS_ERROR: 'MY_DELEGATIONS_ACCESS_ERROR',
};

const auth = new Auth();
const {getAccessToken} = auth;

function headers() {
  return {'Accept': 'application/json', 'Content-Type': 'application/json', 'x-access-token': getAccessToken()};
}

const API = process.env.REACT_APP_API_URL || '';

export function loadDelegations() {
  return function(dispatch) {
    dispatch({type: myDelegationsActions.LOAD_REQUEST});
    axios.get(`${API}/userProfile/delegations/me`, {headers: headers()})
        .then((res) => {
          dispatch({type: myDelegationsActions.LOAD_SUCCESS, payload: (res.data.response && res.data.response.value) || []});
        })
        .catch((error) => {
          dispatch({type: myDelegationsActions.LOAD_FAILURE, payload: String(error.message || error)});
        });
  };
}

export function loadUsers() {
  return function(dispatch) {
    axios.get(`${API}/userProfile/users`, {headers: headers()})
        .then((res) => {
          dispatch({type: myDelegationsActions.LOAD_USERS_SUCCESS, payload: (res.data.response && res.data.response.value) || []});
        })
        .catch((error) => {
          dispatch({type: myDelegationsActions.MUTATION_FAILURE, payload: String(error.message || error)});
        });
  };
}

export function createDelegation(payload) {
  return function(dispatch) {
    axios.post(`${API}/userProfile/delegations`, payload, {headers: headers()})
        .then(() => {
          dispatch({type: myDelegationsActions.MUTATION_SUCCESS, payload: 'Delegation created'});
          dispatch(loadDelegations());
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.error) || String(error.message || error);
          dispatch({type: myDelegationsActions.MUTATION_FAILURE, payload: msg});
        });
  };
}

export function revokeDelegation(id) {
  return function(dispatch) {
    axios.delete(`${API}/userProfile/delegations/${id}`, {headers: headers()})
        .then(() => {
          dispatch({type: myDelegationsActions.MUTATION_SUCCESS, payload: 'Delegation revoked'});
          dispatch(loadDelegations());
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.error) || String(error.message || error);
          dispatch({type: myDelegationsActions.MUTATION_FAILURE, payload: msg});
        });
  };
}

export function clearMessage() {
  return {type: myDelegationsActions.CLEAR_MESSAGE};
}

// ── Slice 4c — access delegations (mockup S6) and standing nominations (S7) ──────────────────

export function loadAccessDelegations() {
  return function(dispatch) {
    axios.get(`${API}/access/delegations/me`, {headers: headers()})
        .then((res) => {
          dispatch({type: myDelegationsActions.ACCESS_LOAD_SUCCESS, payload: res.data || {}});
        })
        .catch((error) => {
          dispatch({type: myDelegationsActions.ACCESS_ERROR, payload: String(error.message || error)});
        });
  };
}

export function loadDelegablePairs() {
  return function(dispatch) {
    axios.get(`${API}/access/delegations/delegable`, {headers: headers()})
        .then((res) => {
          dispatch({type: myDelegationsActions.ACCESS_DELEGABLE_SUCCESS, payload: (res.data || {}).pairs || []});
        })
        .catch((error) => {
          dispatch({type: myDelegationsActions.ACCESS_ERROR, payload: String(error.message || error)});
        });
  };
}

// A 403 here is the UN-PROVISIONED state, not a failure. `GET /access/delegates` is gated on
// `access.delegation:create`, which §3.6 grants per person — so for most users this route denies,
// and the honest response is a disabled action with a reason, never an error toast.
export function loadDelegates() {
  return function(dispatch) {
    axios.get(`${API}/access/delegates`, {headers: headers()})
        .then((res) => {
          dispatch({type: myDelegationsActions.ACCESS_DELEGATES_SUCCESS, payload: (res.data || {}).delegates || []});
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            return dispatch({type: myDelegationsActions.ACCESS_NOT_PROVISIONED});
          }
          dispatch({type: myDelegationsActions.ACCESS_ERROR, payload: String(error.message || error)});
        });
  };
}

export function openAccessDrawer(kind) {
  return {type: myDelegationsActions.ACCESS_DRAWER_OPEN, payload: kind};
}

export function closeAccessDrawer() {
  return {type: myDelegationsActions.ACCESS_DRAWER_CLOSE};
}

// One thunk for both shapes. A nomination is a delegation with no end date — §12.4: the window
// starts at activation, not now — so the only difference on the wire is the endpoint.
export function createAccessDelegation(payload, kind) {
  return function(dispatch) {
    const path = kind === 'standing_delegation' ? 'nominations' : 'delegations';
    axios.post(`${API}/access/${path}`, payload, {headers: headers()})
        .then(() => {
          dispatch({type: myDelegationsActions.ACCESS_DRAWER_CLOSE});
          dispatch({type: myDelegationsActions.MUTATION_SUCCESS,
            payload: kind === 'standing_delegation' ? 'Nomination saved' : 'Delegation created'});
          dispatch(loadAccessDelegations());
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.error) || String(error.message || error);
          dispatch({type: myDelegationsActions.ACCESS_ERROR, payload: msg});
        });
  };
}

export function revokeAccessDelegation(delegationId) {
  return function(dispatch) {
    axios.delete(`${API}/access/delegations/${delegationId}`, {headers: headers()})
        .then(() => {
          dispatch({type: myDelegationsActions.MUTATION_SUCCESS, payload: 'Delegation ended'});
          dispatch(loadAccessDelegations());
        })
        .catch((error) => {
          const msg = (error.response && error.response.data && error.response.data.error) || String(error.message || error);
          dispatch({type: myDelegationsActions.ACCESS_ERROR, payload: msg});
        });
  };
}
